import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";

const LoginPage = lazy(() =>
  import("@/pages/login").then((module) => ({ default: module.LoginPage })),
);
const PublicPortfolioPage = lazy(() =>
  import("./pages/public-portfolio").then((module) => ({
    default: module.PublicPortfolioPage,
  })),
);
const PublicCaseStudyPage = lazy(() =>
  import("./pages/public-case-study").then((module) => ({
    default: module.PublicCaseStudyPage,
  })),
);
const PublicWorkPage = lazy(() =>
  import("./pages/public-work").then((module) => ({
    default: module.PublicWorkPage,
  })),
);
const PublicBlogsPage = lazy(() =>
  import("./pages/public-blogs").then((module) => ({
    default: module.PublicBlogsPage,
  })),
);
const ClonePage = lazy(() =>
  import("@portfolio/clone/clone-page").then((module) => ({
    default: module.ClonePage,
  })),
);

interface ProtectedRouteProps {
  children: ReactNode;
}

function LoadingRoute(): ReactNode {
  return <main className="route-shell">Loading...</main>;
}

function ProtectedRoute({ children }: ProtectedRouteProps): ReactNode {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);

  if (isLoading) return <LoadingRoute />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return children;
}

function PublicRoute({ children }: ProtectedRouteProps): ReactNode {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);

  if (isLoading) return <LoadingRoute />;
  if (isAuthenticated) return <Navigate to="/login" replace />;

  return children;
}

export default function App(): ReactNode {
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  return (
    <Suspense fallback={<LoadingRoute />}>
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="/p/:slug" element={<PublicPortfolioPage />} />
        <Route path="/p/:slug/chat" element={<ClonePage />} />
        <Route path="/p/:slug/work" element={<PublicWorkPage />} />
        <Route path="/p/:slug/blogs" element={<PublicBlogsPage />} />
        <Route path="/p/:slug/work/:csSlug" element={<PublicCaseStudyPage />} />
        <Route path="/p/:slug/case/:csSlug" element={<PublicCaseStudyPage />} />
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <Navigate to="/login" replace />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}
